/**
 * AiProviderCard — whose key pays for AI, which model the design agent
 * spends on, and what is left in the pot.
 *
 * Three questions, one surface:
 *   1. PROVIDER — platform credits, or the operator's own Anthropic key.
 *      A pasted key is verified against the real API BEFORE it is stored
 *      (the server answers 422 with Anthropic's own message, which is
 *      shown inline). The key itself never comes back: the server sends
 *      only the last four characters, and this component never renders,
 *      logs or re-echoes what was typed — the input is cleared the moment
 *      the save succeeds.
 *   2. DESIGN-AGENT MODEL — the deployment default for Studio chat. A
 *      model that rejects a FORCED tool call (Fable 5.1 today) is listed
 *      but not selectable: the agent forces a tool on every turn, so it
 *      would fail at the first message rather than degrade.
 *   3. CREDITS — balance, spend, and a manual top-up. Balance may go
 *      NEGATIVE by design; we never block a render for money.
 *
 * Rendered only when `features.aiSettings` is on (QL_AI_SETTINGS); the
 * old read-only Settings sections stay as the fallback.
 */
import { AlertTriangle, Check, Key, Loader2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteOwnKey,
  getAiSettings,
  putAiSettings,
  topUpCredits,
  type AiSettingsInfo,
} from "../api";
import { formatUsd } from "../lib/chatThreads";
import { modelOptionLabel, modelPriceLine } from "../lib/modelCopy";
import { cardStyle, ctaSmallStyle, inputStyle } from "./styles";
import { Select } from "./ui/Select";

const MIN_TOPUP_USD = 1;
const MAX_TOPUP_USD = 10_000;

function Card({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div style={{ ...cardStyle, padding: 24, marginBottom: 14 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4, color: "var(--ql-fg)" }}>
        {title}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginBottom: 16, lineHeight: 1.55 }}>
          {sub}
        </div>
      )}
      {children}
    </div>
  );
}

function Notice({ kind, text }: { kind: "error" | "ok"; text: string }): React.JSX.Element {
  const error = kind === "error";
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "flex-start",
        marginTop: 12,
        padding: "9px 11px",
        borderRadius: 10,
        fontSize: 12.5,
        lineHeight: 1.5,
        border: `1px solid ${error ? "rgba(248,113,113,.30)" : "rgba(99,226,183,.35)"}`,
        background: error ? "rgba(248,113,113,.07)" : "rgba(99,226,183,.07)",
        color: error ? "var(--ql-danger)" : "var(--ql-fg)",
      }}
    >
      {error ? (
        <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 2 }} />
      ) : (
        <Check size={13} style={{ flexShrink: 0, marginTop: 2, color: "rgb(99,226,183)" }} />
      )}
      <span style={{ minWidth: 0, wordBreak: "break-word" }}>{text}</span>
    </div>
  );
}

const optionRowStyle = (picked: boolean, busy = false): React.CSSProperties => ({
  flex: 1,
  padding: 14,
  borderRadius: 12,
  background: picked ? "rgba(255,213,0,.06)" : "rgba(255,255,255,.02)",
  border: picked ? "1px solid rgba(255,213,0,.30)" : "1px solid rgba(255,255,255,.06)",
  cursor: busy ? "default" : "pointer",
  opacity: busy ? 0.75 : 1,
  display: "flex",
  alignItems: "center",
  gap: 12,
});

export function AiProviderCard(): React.JSX.Element {
  const [info, setInfo] = useState<AiSettingsInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  /**
   * In-flight guard, in a REF rather than in state.
   *
   * A `if (busyState) return` guard does not actually hold: three clicks in
   * one task all read the same pre-update closure, and a `disabled` prop only
   * bites after React re-renders — measured live, a triple-click on "Remove
   * key" fired THREE DELETEs. A ref is written synchronously inside the first
   * handler, so the second call sees it in the same tick.
   */
  const busyRef = useRef<Record<string, boolean>>({});
  const claim = (op: string): boolean => {
    if (busyRef.current[op]) return false;
    busyRef.current[op] = true;
    return true;
  };
  const release = (op: string): void => {
    busyRef.current[op] = false;
  };

  // Provider
  const [keyInput, setKeyInput] = useState("");
  const [savingKey, setSavingKey] = useState(false);
  /** The mode being switched TO while the PUT is in flight — doubles as the
   *  in-flight guard and as the optimistic highlight, so the radio moves the
   *  moment it is clicked instead of looking dead for a round trip. */
  const [pendingMode, setPendingMode] = useState<"platform" | "own" | null>(null);
  const [removingKey, setRemovingKey] = useState(false);
  const [providerError, setProviderError] = useState<string | null>(null);
  const [providerOk, setProviderOk] = useState<string | null>(null);

  // Model
  const [savingModel, setSavingModel] = useState(false);
  const [modelError, setModelError] = useState<string | null>(null);

  // Credits
  const [topupUsd, setTopupUsd] = useState("");
  const [topupNote, setTopupNote] = useState("");
  const [toppingUp, setToppingUp] = useState(false);
  const [topupError, setTopupError] = useState<string | null>(null);
  const [topupOk, setTopupOk] = useState<string | null>(null);

  const load = useCallback(() => {
    let alive = true;
    getAiSettings()
      .then((s) => { if (alive) { setInfo(s); setLoadError(null); } })
      .catch((err: unknown) => {
        if (alive) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => load(), [load]);

  const setMode = useCallback(
    async (mode: "platform" | "own") => {
      // Repeat clicks used to queue duplicate PUTs; the first one owns the
      // switch until it answers.
      if (!claim("mode")) return;
      setPendingMode(mode);
      setProviderError(null);
      setProviderOk(null);
      try {
        setInfo(await putAiSettings({ provider: { mode } }));
      } catch (err) {
        setProviderError(err instanceof Error ? err.message : String(err));
      } finally {
        release("mode");
        setPendingMode(null);
      }
    },
    [],
  );

  const saveKey = useCallback(async () => {
    const key = keyInput.trim();
    if (!key || !claim("key")) return;
    setSavingKey(true);
    setProviderError(null);
    setProviderOk(null);
    try {
      // Pasting a key IS choosing it — the server verifies before storing.
      const next = await putAiSettings({ provider: { mode: "own", ownKey: key } });
      setInfo(next);
      setKeyInput(""); // never keep key material around, not even in state
      setProviderOk("Key verified and saved. Anthropic bills you directly from now on.");
    } catch (err) {
      setProviderError(err instanceof Error ? err.message : String(err));
    } finally {
      release("key");
      setSavingKey(false);
    }
  }, [keyInput]);

  const removeKey = useCallback(async () => {
    if (!claim("removeKey")) return; // a second DELETE would race the first
    setRemovingKey(true);
    setProviderError(null);
    setProviderOk(null);
    try {
      setInfo(await deleteOwnKey());
      setProviderOk("Key removed — platform credits are paying again.");
    } catch (err) {
      setProviderError(err instanceof Error ? err.message : String(err));
    } finally {
      release("removeKey");
      setRemovingKey(false);
    }
  }, []);

  const saveModel = useCallback(async (id: string) => {
    if (!claim("model")) return;
    setSavingModel(true);
    setModelError(null);
    try {
      setInfo(await putAiSettings({ designAgent: { model: id === "" ? null : id } }));
    } catch (err) {
      setModelError(err instanceof Error ? err.message : String(err));
    } finally {
      release("model");
      setSavingModel(false);
    }
  }, []);

  const addCredit = useCallback(async () => {
    const usd = Number(topupUsd);
    setTopupError(null);
    setTopupOk(null);
    // The floor is 1, not 0 — the message says "between 1 and 10000" and a
    // $0.50 top-up would have sailed through it.
    if (!Number.isFinite(usd) || usd < MIN_TOPUP_USD || usd > MAX_TOPUP_USD) {
      setTopupError(`Enter an amount between ${MIN_TOPUP_USD} and ${MAX_TOPUP_USD}.`);
      return;
    }
    if (!claim("topup")) return; // two clicks must never charge twice
    setToppingUp(true);
    try {
      const credits = await topUpCredits(usd, topupNote.trim() || undefined);
      setInfo((cur) => (cur ? { ...cur, credits } : cur));
      setTopupUsd("");
      setTopupNote("");
      setTopupOk(`Added ${formatUsd(usd)} — balance is now ${formatUsd(credits.balanceUsd)}.`);
    } catch (err) {
      setTopupError(err instanceof Error ? err.message : String(err));
    } finally {
      release("topup");
      setToppingUp(false);
    }
  }, [topupUsd, topupNote]);

  if (loadError) {
    return (
      <Card title="AI provider" sub="Who pays for the models this deployment runs.">
        <Notice kind="error" text={`Couldn't read AI settings: ${loadError}`} />
        <button type="button" onClick={() => load()} style={{ ...ctaSmallStyle, marginTop: 12 }}>
          Try again
        </button>
      </Card>
    );
  }

  if (!info) {
    return (
      <Card title="AI provider">
        <div style={{ display: "flex", alignItems: "center", gap: 9, fontSize: 13, color: "var(--ql-fg-muted)" }}>
          <Loader2 size={14} className="ql-spin" />
          Loading…
        </div>
      </Card>
    );
  }

  // Optimistic: the radio follows the click, the server confirms it after.
  const own = (pendingMode ?? info.provider.mode) === "own";
  const switching = pendingMode !== null;
  const hasKey = !!info.provider.ownKeyLast4;
  const balance = info.credits.balanceUsd;
  const defaultLabel =
    info.models.find((m) => m.id === info.defaultModel)?.label ?? info.defaultModel;
  const selectedModel = info.models.find((m) => m.id === (info.designAgent.model ?? info.defaultModel));
  const selectedPriceLine = selectedModel ? modelPriceLine(selectedModel) : null;

  return (
    <>
      {/* No "switched off" branch here on purpose: Settings only mounts this
          card when `features.aiSettings` is true, and that IS `info.enabled`
          (both are `QL_AI_SETTINGS !== "off"`), so the branch could never
          render. The disabled case is handled one level up, in Settings. */}

      <Card
        title="Who pays for AI"
        sub="Platform credits are metered per call against the balance below. Your own key is billed by Anthropic directly — we never see the invoice, and we never store the key in the clear."
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <label style={optionRowStyle(!own, switching)}>
            <input
              type="radio"
              name="ai-provider-mode"
              checked={!own}
              disabled={switching}
              onChange={() => void setMode("platform")}
              style={{ accentColor: "var(--ql-yellow)" }}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Platform credits</div>
              <div style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>
                Metered per call · balance {formatUsd(balance)}
              </div>
            </div>
            {pendingMode === "platform" && <Loader2 size={13} className="ql-spin" />}
          </label>
          <label style={optionRowStyle(own, switching || !hasKey)}>
            <input
              type="radio"
              name="ai-provider-mode"
              checked={own}
              onChange={() => void setMode("own")}
              disabled={!hasKey || switching}
              style={{ accentColor: "var(--ql-yellow)" }}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>My own Anthropic key</div>
              <div style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>
                {hasKey ? `Stored ····${info.provider.ownKeyLast4}` : "Add a key below first"}
              </div>
            </div>
            {pendingMode === "own" && <Loader2 size={13} className="ql-spin" />}
          </label>
        </div>

        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: ".06em",
              textTransform: "uppercase",
              color: "var(--ql-fg-muted)",
              marginBottom: 8,
            }}
          >
            Anthropic key
          </div>
          {hasKey && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 12px",
                borderRadius: 11,
                marginBottom: 10,
                background: "rgba(255,255,255,.02)",
                border: "1px solid rgba(255,255,255,.06)",
              }}
            >
              <Key size={14} style={{ color: "var(--ql-yellow)", flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, fontFamily: "var(--ql-font-mono)" }}>
                  ····{info.provider.ownKeyLast4}
                </div>
                <div style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>
                  {info.provider.ownKeyOkAt
                    ? `Verified ${new Date(info.provider.ownKeyOkAt).toLocaleString()}`
                    : "Stored"}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void removeKey()}
                disabled={removingKey}
                title="Forget this key and go back to platform credits"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "7px 12px",
                  borderRadius: 10,
                  border: "1px solid rgba(248,113,113,.35)",
                  background: "rgba(248,113,113,.07)",
                  color: "var(--ql-danger)",
                  fontFamily: "inherit",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: removingKey ? "default" : "pointer",
                  opacity: removingKey ? 0.6 : 1,
                }}
              >
                {removingKey ? <Loader2 size={12} className="ql-spin" /> : <Trash2 size={12} />}
                {removingKey ? "Removing…" : "Remove key"}
              </button>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder={hasKey ? "Paste a new key to replace it" : "sk-ant-…"}
              aria-label="Your Anthropic API key"
              autoComplete="off"
              spellCheck={false}
              style={{ ...inputStyle, flex: 1, minWidth: 220, fontFamily: "var(--ql-font-mono)" }}
            />
            <button
              type="button"
              onClick={() => void saveKey()}
              disabled={!keyInput.trim() || savingKey}
              style={{
                ...ctaSmallStyle,
                opacity: !keyInput.trim() || savingKey ? 0.5 : 1,
                cursor: !keyInput.trim() || savingKey ? "default" : "pointer",
              }}
            >
              {savingKey ? <Loader2 size={13} className="ql-spin" /> : <Check size={13} />}
              {savingKey ? "Verifying…" : "Verify & save"}
            </button>
          </div>
          <div style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", marginTop: 8, lineHeight: 1.5 }}>
            The key is checked against the Anthropic API before it is stored, and only its last four
            characters are ever sent back to this screen.
          </div>
          {hasKey && info.provider.keyEncrypted === false && (
            <Notice
              kind="error"
              text="Stored without encryption on this server — set a session secret."
            />
          )}
          {providerError && <Notice kind="error" text={providerError} />}
          {providerOk && <Notice kind="ok" text={providerOk} />}
        </div>
      </Card>

      <Card
        title="Design agent model"
        sub={`Which model answers in Studio chat. Deployment default: ${defaultLabel}.`}
      >
        <Select
          value={info.designAgent.model ?? ""}
          disabled={savingModel}
          onChange={(v) => void saveModel(v)}
          aria-label="Which model answers in Studio chat"
          size="lg"
          options={[
            { value: "", label: `Deployment default — ${defaultLabel}` },
            // The price rides on the row as a muted hint; a model with no
            // known price gets no hint rather than a wrong number.
            ...info.models.map((m) => ({
              value: m.id,
              label: modelOptionLabel(m),
              disabled: !m.forcedToolUse,
              hint: modelPriceLine(m) ?? undefined,
            })),
          ]}
        />
        {selectedPriceLine && (
          <div style={{ fontSize: 11, color: "var(--ql-fg-faint)", marginTop: 6, fontFamily: "var(--ql-font-mono)" }}>
            {selectedPriceLine}
          </div>
        )}
        <div style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", marginTop: 8, lineHeight: 1.5 }}>
          Some models can’t drive the editor — they’re listed so you know they exist, but they can’t
          answer here.
        </div>
        {modelError && <Notice kind="error" text={modelError} />}
      </Card>

      <Card
        title="Credits"
        sub="Manual top-up — a payment page comes later. Only platform-key calls debit this balance; your own key is billed by Anthropic."
      >
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          {[
            { lbl: "Balance", val: formatUsd(balance), danger: balance < 0 },
            { lbl: "Spent", val: formatUsd(info.credits.spentUsd), danger: false },
            { lbl: "Top-ups", val: String(info.credits.topups.length), danger: false },
          ].map((t) => (
            <div
              key={t.lbl}
              style={{
                flex: 1,
                minWidth: 120,
                padding: 14,
                borderRadius: 12,
                background: "rgba(255,255,255,.02)",
                border: `1px solid ${t.danger ? "rgba(248,113,113,.35)" : "rgba(255,255,255,.06)"}`,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: ".06em",
                  textTransform: "uppercase",
                  color: "var(--ql-fg-muted)",
                  marginBottom: 6,
                }}
              >
                {t.lbl}
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 800,
                  fontFamily: "var(--ql-font-mono)",
                  color: t.danger ? "var(--ql-danger)" : "var(--ql-fg)",
                }}
              >
                {t.val}
              </div>
            </div>
          ))}
        </div>

        {balance < 0 && (
          <Notice
            kind="error"
            text="The balance is negative. Renders and chats keep running — we never block work for money — but add credit when you can."
          />
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
          <input
            type="number"
            min={1}
            max={MAX_TOPUP_USD}
            step={1}
            value={topupUsd}
            onChange={(e) => setTopupUsd(e.target.value)}
            placeholder="Amount (USD)"
            aria-label="Credit to add, in US dollars"
            style={{ ...inputStyle, width: 160, flex: "0 0 auto" }}
          />
          <input
            type="text"
            value={topupNote}
            onChange={(e) => setTopupNote(e.target.value)}
            placeholder="Note (optional)"
            aria-label="Note for this credit (optional)"
            maxLength={120}
            style={{ ...inputStyle, flex: 1, minWidth: 180 }}
          />
          <button
            type="button"
            onClick={() => void addCredit()}
            disabled={toppingUp || !topupUsd.trim()}
            style={{
              ...ctaSmallStyle,
              opacity: toppingUp || !topupUsd.trim() ? 0.5 : 1,
              cursor: toppingUp || !topupUsd.trim() ? "default" : "pointer",
            }}
          >
            {toppingUp ? <Loader2 size={13} className="ql-spin" /> : null}
            {toppingUp ? "Adding…" : "Add credit"}
          </button>
        </div>
        {topupError && <Notice kind="error" text={topupError} />}
        {topupOk && <Notice kind="ok" text={topupOk} />}

        {info.credits.topups.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: "var(--ql-fg-muted)",
                marginBottom: 8,
              }}
            >
              Recent top-ups
            </div>
            {[...info.credits.topups]
              .slice(-5)
              .reverse()
              .map((t, i) => (
                <div
                  key={`${t.at}-${i}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 2px",
                    fontSize: 12.5,
                    color: "var(--ql-fg-muted)",
                    borderTop: i === 0 ? "none" : "1px solid rgba(255,255,255,.05)",
                  }}
                >
                  <span style={{ fontFamily: "var(--ql-font-mono)", color: "var(--ql-fg)" }}>
                    {formatUsd(t.usd)}
                  </span>
                  <span>{t.at ? new Date(t.at).toLocaleString() : "—"}</span>
                  {t.note && <span style={{ minWidth: 0, wordBreak: "break-word" }}>· {t.note}</span>}
                </div>
              ))}
          </div>
        )}
      </Card>
    </>
  );
}
